import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tooltip,
  Skeleton,
  Alert,
  Pagination,
  SelectChangeEvent,
  Stack,
  Grid,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  Search,
  Visibility,
  Edit,
  Refresh,
  Add,
  Delete,
  CalendarMonth,
  AccessTime,
  BookOnline,
} from "@mui/icons-material";
import { bookingsAPI, availabilityAPI, BookingFilters, AvailabilitySchedule, TimeRange } from "../../services/adminApi";

interface Booking {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  sessionType: "private" | "group";
  preferredDate: string;
  preferredTime: string;
  notes?: string;
  status: "pending" | "accepted" | "rejected" | "completed" | "cancelled";
  assignedDate?: string;
  assignedTime?: string;
  adminNotes?: string;
  user?: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

const AdminBookings: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BookingFilters>({
    page: 1,
    limit: 20,
  });
  const [total, setTotal] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    status: "pending" as Booking["status"],
    assignedDate: "",
    assignedTime: "",
    adminNotes: "",
  });

  // â”€â”€ Availability state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [schedules, setSchedules] = useState<AvailabilitySchedule[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);
  const [slotDialog, setSlotDialog] = useState(false);
  const [slotForm, setSlotForm] = useState<Omit<AvailabilitySchedule, "_id">>({
    dayOfWeek: 1,
    sessionType: "group",
    timeRanges: [{ start: "09:00", end: "11:00" }],
    isActive: true,
  });

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookingsAPI.getBookings(filters);
      setBookings(response.data.bookings);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.message || "Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleFilterChange = (key: keyof BookingFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setEditForm({
      status: booking.status,
      assignedDate: booking.assignedDate || "",
      assignedTime: booking.assignedTime || "",
      adminNotes: booking.adminNotes || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBooking) return;

    try {
      await bookingsAPI.updateBooking(selectedBooking._id, editForm);
      setEditDialogOpen(false);
      setSelectedBooking(null);
      fetchBookings();
    } catch (err: any) {
      setError(err.message || "Failed to update booking");
    }
  };

  const getStatusColor = (status: Booking["status"]) => {
    switch (status) {
      case "pending": return "warning";
      case "accepted": return "success";
      case "rejected": return "error";
      case "completed": return "info";
      case "cancelled": return "default";
      default: return "default";
    }
  };

  // â”€â”€ Availability helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const fetchSchedules = useCallback(async () => {
    setScheduleLoading(true);
    setScheduleError(null);
    try {
      const res = await availabilityAPI.getAll();
      setSchedules(res.data);
    } catch (err: any) {
      setScheduleError(err.message || "Failed to load schedules");
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 1) fetchSchedules();
  }, [tab, fetchSchedules]);

  const handleSaveSchedule = async () => {
    setScheduleError(null);
    try {
      await availabilityAPI.upsert(slotForm);
      setSlotDialog(false);
      setScheduleSuccess("Schedule saved!");
      fetchSchedules();
      setTimeout(() => setScheduleSuccess(null), 3000);
    } catch (err: any) {
      setScheduleError(err.message || "Failed to save schedule");
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await availabilityAPI.delete(id);
      fetchSchedules();
    } catch (err: any) {
      setScheduleError(err.message || "Failed to delete schedule");
    }
  };

  const addTimeRange = () => {
    setSlotForm(prev => ({
      ...prev,
      timeRanges: [...prev.timeRanges, { start: "12:00", end: "14:00" }],
    }));
  };

  const removeTimeRange = (index: number) => {
    setSlotForm(prev => ({
      ...prev,
      timeRanges: prev.timeRanges.filter((_, i) => i !== index),
    }));
  };

  const updateTimeRange = (index: number, field: keyof TimeRange, value: string) => {
    setSlotForm(prev => {
      const ranges = [...prev.timeRanges];
      ranges[index] = { ...ranges[index], [field]: value };
      return { ...prev, timeRanges: ranges };
    });
  };

  const totalPages = Math.ceil(total / (filters.limit || 20));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bookings Management
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab icon={<BookOnline />} iconPosition="start" label="Bookings" />
        <Tab icon={<CalendarMonth />} iconPosition="start" label="Availability Schedule" />
      </Tabs>

      {/* â”€â”€ TAB 0: Bookings List â”€â”€ */}
      {tab === 0 && (
      <>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by name or email"
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ""}
                onChange={(e: SelectChangeEvent) => handleFilterChange("status", e.target.value || undefined)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Session Type</InputLabel>
              <Select
                value={filters.sessionType || ""}
                onChange={(e: SelectChangeEvent) => handleFilterChange("sessionType", e.target.value || undefined)}
                label="Session Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="group">Group</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => setFilters({ page: 1, limit: 20 })}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Session Type</TableCell>
              <TableCell>Preferred Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No bookings found
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell>{booking.name}</TableCell>
                  <TableCell>{booking.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.sessionType}
                      color={booking.sessionType === "private" ? "primary" : "secondary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{booking.preferredDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton onClick={() => handleViewBooking(booking)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditBooking(booking)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={filters.page || 1}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* View Dialog */}
      {selectedBooking && !editDialogOpen && (
        <Dialog
          open={!!selectedBooking}
          onClose={() => setSelectedBooking(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Booking Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Name</Typography>
                <Typography>{selectedBooking.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Email</Typography>
                <Typography>{selectedBooking.email}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Phone</Typography>
                <Typography>{selectedBooking.phone || "N/A"}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Session Type</Typography>
                <Typography>{selectedBooking.sessionType}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Preferred Date</Typography>
                <Typography>{selectedBooking.preferredDate}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2">Preferred Time</Typography>
                <Typography>{selectedBooking.preferredTime}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2">Notes</Typography>
                <Typography>{selectedBooking.notes || "N/A"}</Typography>
              </Grid>
              {selectedBooking.assignedDate && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Assigned Date</Typography>
                  <Typography>{selectedBooking.assignedDate}</Typography>
                </Grid>
              )}
              {selectedBooking.assignedTime && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2">Assigned Time</Typography>
                  <Typography>{selectedBooking.assignedTime}</Typography>
                </Grid>
              )}
              {selectedBooking.adminNotes && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2">Admin Notes</Typography>
                  <Typography>{selectedBooking.adminNotes}</Typography>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedBooking(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Booking</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                onChange={(e: SelectChangeEvent) => setEditForm(prev => ({ ...prev, status: e.target.value as Booking["status"] }))}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Admin Notes"
              multiline
              rows={3}
              value={editForm.adminNotes}
              onChange={(e) => setEditForm(prev => ({ ...prev, adminNotes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      {/* â”€â”€ TAB 1: Availability Schedule â”€â”€ */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Weekly Availability</Typography>
              <Typography variant="body2" color="text.secondary">
                Define which days and time ranges you are available for each session type.
                Slots are automatically split into 1-hour bookable windows.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSlotForm({ dayOfWeek: 1, sessionType: "group", timeRanges: [{ start: "09:00", end: "11:00" }], isActive: true });
                setSlotDialog(true);
              }}
              sx={{ bgcolor: "#28a745", "&:hover": { bgcolor: "#218838" }, minWidth: 180 }}
            >
              Add Schedule
            </Button>
          </Box>

          {scheduleError && <Alert severity="error" sx={{ mb: 2 }}>{scheduleError}</Alert>}
          {scheduleSuccess && <Alert severity="success" sx={{ mb: 2 }}>{scheduleSuccess}</Alert>}

          {scheduleLoading ? (
            <Grid container spacing={2}>
              {[...Array(4)].map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : schedules.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
              <CalendarMonth sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6">No schedules configured</Typography>
              <Typography variant="body2">Click "Add Schedule" to define your weekly availability.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {schedules
                .slice()
                .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                .map((sched) => (
                  <Grid key={sched._id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card
                      elevation={2}
                      sx={{
                        border: sched.isActive ? "2px solid #28a745" : "2px solid transparent",
                        borderRadius: 2,
                        opacity: sched.isActive ? 1 : 0.6,
                        transition: "all 0.2s",
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="h6" fontWeight={700}>
                            {DAY_NAMES[sched.dayOfWeek]}
                          </Typography>
                          <Chip
                            label={sched.sessionType}
                            size="small"
                            color={sched.sessionType === "group" ? "secondary" : sched.sessionType === "private" ? "primary" : "default"}
                          />
                        </Box>
                        <Chip
                          label={sched.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={sched.isActive ? "success" : "default"}
                          sx={{ mb: 1.5 }}
                        />
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                          <AccessTime fontSize="inherit" /> Time Ranges
                        </Typography>
                        {sched.timeRanges.map((tr, idx) => (
                          <Chip
                            key={idx}
                            label={`${tr.start} â€“ ${tr.end}`}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5, fontFamily: "monospace" }}
                          />
                        ))}
                      </CardContent>
                      <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSlotForm({
                                dayOfWeek: sched.dayOfWeek,
                                sessionType: sched.sessionType,
                                timeRanges: sched.timeRanges,
                                isActive: sched.isActive,
                              });
                              setSlotDialog(true);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => sched._id && handleDeleteSchedule(sched._id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          )}

          {/* Add / Edit Schedule Dialog */}
          <Dialog open={slotDialog} onClose={() => setSlotDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>
              <CalendarMonth sx={{ verticalAlign: "middle", mr: 1 }} />
              Configure Availability
            </DialogTitle>
            <DialogContent>
              <Stack spacing={3} sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    value={slotForm.dayOfWeek}
                    onChange={(e: SelectChangeEvent<number>) =>
                      setSlotForm(prev => ({ ...prev, dayOfWeek: Number(e.target.value) }))
                    }
                    label="Day of Week"
                  >
                    {DAY_NAMES.map((name, idx) => (
                      <MenuItem key={idx} value={idx}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Session Type</InputLabel>
                  <Select
                    value={slotForm.sessionType}
                    onChange={(e: SelectChangeEvent) =>
                      setSlotForm(prev => ({ ...prev, sessionType: e.target.value as AvailabilitySchedule["sessionType"] }))
                    }
                    label="Session Type"
                  >
                    <MenuItem value="group">Group Session</MenuItem>
                    <MenuItem value="private">Private Session</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={slotForm.isActive}
                      onChange={(e) => setSlotForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      color="success"
                    />
                  }
                  label="Active"
                />

                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AccessTime fontSize="small" /> Time Ranges
                    </Typography>
                    <Button size="small" startIcon={<Add />} onClick={addTimeRange}>
                      Add Range
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Each range will be split into 1-hour bookable slots automatically.
                  </Typography>
                  {slotForm.timeRanges.map((tr, idx) => (
                    <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                      <TextField
                        label="Start"
                        type="time"
                        value={tr.start}
                        onChange={(e) => updateTimeRange(idx, "start", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <Typography color="text.secondary">â€“</Typography>
                      <TextField
                        label="End"
                        type="time"
                        value={tr.end}
                        onChange={(e) => updateTimeRange(idx, "end", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeTimeRange(idx)}
                        disabled={slotForm.timeRanges.length === 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSlotDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSaveSchedule}
                variant="contained"
                sx={{ bgcolor: "#28a745", "&:hover": { bgcolor: "#218838" } }}
              >
                Save Schedule
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default AdminBookings;

